"use client";

import { AddPhotoAlternate, Close } from "@mui/icons-material";
import styles from "./photo-uploader.module.css";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useDropzone, FileRejection, ErrorCode } from "react-dropzone";
import Image from "next/image";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import { FaEye } from "react-icons/fa6";
import {
  MdArrowBackIosNew,
  MdArrowForwardIos,
  MdDelete,
  MdErrorOutline,
} from "react-icons/md";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import {
  initialState,
  UploaderActionKind,
  uploaderReducer,
} from "./photo-uploader-reducer";
import { handleDeletePhoto, handleUploadListingPhotos } from "./handler";
import { useTranslations } from "next-intl";
import { useEdittingStore } from "../../context/useEdittingStore";
import { ListingTypeEnum } from "@/types/types";

const configBigImage = {
  initialQuality: 0.74,
  maxWidthOrHeight: 1800,
  useWebWorker: true,
  fileType: "image/jpeg",
  alwaysKeepResolution: true,
};

const configSmallImage = {
  initialQuality: 0.9,
  maxWidthOrHeight: 1800,
  useWebWorker: true,
  fileType: "image/jpeg",
  alwaysKeepResolution: true,
};

export default function PhotoUploader() {
  const { userData } = useUserAuthDataContext();
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const numberOfPhotosRef = useRef<number>(0);
  const workerRef = useRef<Worker>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const [hasUploadedPhotos, setHasUploadedPhotos] = useState(false);
  const [uploaderState, uploaderDispatch] = useReducer(
    uploaderReducer,
    initialState
  );
  const t = useTranslations("NewListing.photoUploader");
  const listing_type = useEdittingStore((state) => state.listing.listing_type);
  const photos = useEdittingStore((state) => state.listing.photos);
  const photosError = useEdittingStore(
    (state) => state.validationErrors.photos
  );
  const photosHydrated = useEdittingStore((state) => state.isHydrated);
  const listing_id = useEdittingStore((state) => state.listing.listing_id);
  const setField = useEdittingStore((state) => state.setField);
  const numberOfMaxPhotosRef = useRef(20);
  const [numberOfMaxPhotosUI, setNumberOfMaxPhotosUI] = useState(20);

  useEffect(() => {
    let maxPhotos = 20;
    if (listing_type === ListingTypeEnum.ESSENTIAL) {
      maxPhotos = 20;
    }
    if (listing_type === ListingTypeEnum.PLUS) {
      maxPhotos = 40;
    }
    if (listing_type === ListingTypeEnum.PRIME) {
      maxPhotos = 60;
    }
    if (listing_type === ListingTypeEnum.SPOTLIGHT) {
      maxPhotos = 80;
    }
    numberOfMaxPhotosRef.current = maxPhotos;
    setNumberOfMaxPhotosUI(maxPhotos);
    setField("photos", photos);
  }, [listing_type]);

  useEffect(() => {
    if (!photosHydrated) return;
    if (!hasUploadedPhotos) {
      if (photos.length > 0 && !isHydrated) {
        uploaderDispatch({
          type: UploaderActionKind.INIT_SAVED_STATE,
          distributionUrls: photos,
        });
        setIsHydrated(true);
      }
    }
  }, [photos, photosHydrated]);

  useEffect(() => {
    workerRef.current = new Worker(
      new URL("./compressWorker.ts", import.meta.url)
    );
    workerRef.current.onmessage = (
      event: MessageEvent<{
        success: boolean;
        file?: Blob;
        error?: string;
        fileName?: string;
      }>
    ) => {
      if (event.data.success && event.data.file) {
        const compressedBlob = event.data.file;
        const uuidString = uuidv4();
        const compressedFile = new File(
          [compressedBlob],
          `${uuidString}.jpeg`,
          { type: "image/jpeg" }
        );
        const imageUrl = URL.createObjectURL(compressedBlob);
        uploaderDispatch({
          type: UploaderActionKind.ADDRESIZEDPHOTO,
          newPhoto: {
            file: compressedFile,
            resizedImageSrc: imageUrl,
            isUploaded: false,
          },
        });
      } else if (!event.data.success && event.data.fileName) {
        uploaderDispatch({
          type: UploaderActionKind.DECREASE_NUMBER_OF_PHOTOS,
        });
        toast(`${t("unableToUpload")}: ${event.data.fileName}.`, {
          type: "error",
        });
      } else {
        uploaderDispatch({
          type: UploaderActionKind.DECREASE_NUMBER_OF_PHOTOS,
        });
        toast(`${t("unableToUpload")}.`, {
          type: "error",
        });
      }
    };
    return () => {
      workerRef.current?.terminate();
    };
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length !== 0) {
        switch (rejectedFiles[0].errors[0].code) {
          case ErrorCode.FileInvalidType:
            toast(t("formatError"), {
              type: "error",
            });
            break;
          case ErrorCode.FileTooLarge:
            toast(t("tooLargeError"), {
              type: "error",
            });
            break;
          case ErrorCode.TooManyFiles:
            toast(
              t("tooManyFiles", { maxPhotos: numberOfMaxPhotosRef.current }),
              {
                type: "error",
              }
            );
            break;
          case ErrorCode.FileTooSmall:
            toast(t("fileTooSmall"), {
              type: "error",
            });
            break;
          default:
            toast(t("unknownError"), {
              type: "error",
            });
            break;
        }
        return;
      }
      const acceptedFilesLength = acceptedFiles.length;
      if (
        numberOfPhotosRef.current + acceptedFilesLength >
        numberOfMaxPhotosRef.current
      ) {
        toast(t("tooManyFiles", { maxPhotos: numberOfMaxPhotosRef.current }), {
          type: "error",
        });
        return;
      }

      uploaderDispatch({
        type: UploaderActionKind.INCREASE_NUMBER_OF_PHOTOS,
        addedNumber: acceptedFilesLength,
      });
      setHasUploadedPhotos(true);
      setIsHydrated(true);

      acceptedFiles.forEach((file) => {
        if (file.size < 200 * 1024) {
          uploaderDispatch({
            type: UploaderActionKind.INCREASE_NUMBER_OF_RESIZING,
          });
          workerRef.current?.postMessage({
            file,
            config: configSmallImage,
          });
        } else {
          uploaderDispatch({
            type: UploaderActionKind.INCREASE_NUMBER_OF_RESIZING,
          });
          workerRef.current?.postMessage({
            file,
            config: configBigImage,
          });
        }
      });
    },
    []
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    maxFiles: 20,
    maxSize: 10 * 1024 * 1024,
    minSize: 80 * 1024,
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300,
        tolerance: 8,
      },
    })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!uploaderState.canDrag) {
      return;
    }
    if (active.id !== over.id) {
      const oldIndex = uploaderState.photosArray.findIndex(
        (photo) => photo.resizedImageSrc === active.id
      );
      const newIndex = uploaderState.photosArray.findIndex(
        (photo) => photo.resizedImageSrc === over.id
      );
      uploaderDispatch({
        type: UploaderActionKind.REORDER_PHOTO,
        oldIndex,
        newIndex,
      });
    }
  };

  useEffect(() => {
    numberOfPhotosRef.current = uploaderState.numberOfPhotos;
  }, [uploaderState.numberOfPhotos]);

  useEffect(() => {
    if (
      uploaderState.numberOfPhotos === uploaderState.photosArray.length &&
      uploaderState.photosArray.length !== 0
    ) {
      (async () => {
        await handleUploadListingPhotos(
          listing_id,
          uploaderState.photosArray,
          userData.sessionId,
          (resizedImageSrc: string, distributionSrc: string) => {
            uploaderDispatch({
              type: UploaderActionKind.UPDATE_IS_UPLOADED,
              resizedImageSrc: resizedImageSrc,
              distributionSrc: distributionSrc,
              isUploaded: true,
            });
          }
        );
      })();
      uploaderDispatch({
        type: UploaderActionKind.RESET_NUMBER_OF_RESIZING,
      });
      uploaderDispatch({
        type: UploaderActionKind.UPDATE_CAN_DRAG,
        canDrag: true,
      });
    }
  }, [uploaderState.photosArray.length]);

  useEffect(() => {
    if (!isHydrated) return;
    const distributionsArray = uploaderState.photosArray.map((photo) => {
      if (photo.isUploaded && photo.distributionSrc) {
        return photo.distributionSrc;
      }
    });
    if (distributionsArray) {
      setField("photos", distributionsArray as string[]);
    }
  }, [uploaderState.photosArray]);

  return (
    <>
      <h3 className={`${styles.fieldTitle2} ${photosError && styles.error}`}>
        {t("addPhotos")}*
      </h3>
      {photosError ? (
        <p className={styles.errorText}>
          {photosError === "tooManyPhotos"
            ? t("tooManyPhotos", { maxPhotos: numberOfMaxPhotosUI })
            : t(photosError)}
          <span>
            <MdErrorOutline />
          </span>
        </p>
      ) : (
        <p className={styles.fieldDescription}>{t("addPhotosSubtitle")}</p>
      )}
      <div className={styles.photoUploader}>
        <div
          className={`${styles.addPhotoWrapper} ${photosError && styles.error}`}
          {...(uploaderState.numberOfPhotos === 0 ? getRootProps() : {})}
        >
          {uploaderState.numberOfPhotos === 0 && <input {...getInputProps()} />}
          {uploaderState.numberOfPhotos > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
            >
              <SortableContext
                items={uploaderState.photosArray.map(
                  (photo) => photo.resizedImageSrc
                )}
                strategy={horizontalListSortingStrategy}
              >
                <div className={styles.imagesList} {...getRootProps()}>
                  <input {...getInputProps()} />
                  {uploaderState.photosArray.map((image, index) => (
                    <SortableImage
                      key={image.resizedImageSrc}
                      image={image.resizedImageSrc}
                      index={index}
                      onPreview={() => {
                        document.body.style.overflow = "hidden";
                        document.documentElement.style.overflow = "hidden";
                        setPreviewImage(
                          uploaderState.photosArray[index].resizedImageSrc
                        );
                      }}
                      onRemove={async () => {
                        if (image.distributionSrc) {
                          await handleDeletePhoto(
                            listing_id,
                            image.distributionSrc,
                            userData.sessionId,
                            () => {
                              uploaderDispatch({
                                type: UploaderActionKind.REMOVE_PHOTO,
                                resizedImageSrc: image.resizedImageSrc,
                              });
                            },
                            () => {
                              uploaderDispatch({
                                type: UploaderActionKind.ADD_IMAGE_BACK_ON_POSITION,
                                index: index,
                                photo: image,
                              });
                            }
                          );
                        } else {
                          uploaderDispatch({
                            type: UploaderActionKind.REMOVE_PHOTO,
                            resizedImageSrc: image.resizedImageSrc,
                          });
                        }
                      }}
                    />
                  ))}
                  {Array.from(
                    { length: uploaderState.numberOfResizingPhotos },
                    (_, index) => (
                      <div key={index} className={styles.isLoading}></div>
                    )
                  )}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <>
              <AddPhotoAlternate />
              {isDragActive ? (
                <span className={styles.addPhotoDescription}>
                  {t("dropPhotosHere")}
                </span>
              ) : (
                <span className={styles.addPhotoDescription}>
                  {t("addPhotosDescription", {
                    maxPhotos: numberOfMaxPhotosUI,
                  })}
                </span>
              )}
            </>
          )}
        </div>
        {uploaderState.numberOfPhotos > 0 && (
          <button className={styles.addMoreButton} {...getRootProps()}>
            {t("addMore")}
          </button>
        )}
        {previewImage && (
          <div className={styles.imagePreviewer}>
            <div className={styles.previewWrapper}>
              <div className={styles.nextBackButtonsWrapper}>
                <button
                  className={styles.backButton}
                  onClick={async (e) => {
                    const currIndex = uploaderState.photosArray.findIndex(
                      (photo) => photo.resizedImageSrc === previewImage
                    );
                    if (currIndex > 0) {
                      setPreviewImage(
                        uploaderState.photosArray[currIndex - 1].resizedImageSrc
                      );
                    }
                  }}
                >
                  <MdArrowBackIosNew />
                </button>
                <button
                  className={styles.nextButton}
                  onClick={async (e) => {
                    const currIndex = uploaderState.photosArray.findIndex(
                      (photo) => photo.resizedImageSrc === previewImage
                    );
                    if (currIndex < uploaderState.photosArray.length - 1) {
                      setPreviewImage(
                        uploaderState.photosArray[currIndex + 1].resizedImageSrc
                      );
                    }
                  }}
                >
                  <MdArrowForwardIos />
                </button>
              </div>
              <button
                className={styles.closePreviewButton}
                onClick={(e) => {
                  document.body.style.overflow = "auto";
                  document.documentElement.style.overflow = "auto";
                  setPreviewImage(null);
                }}
              >
                <Close />
              </button>
              <Image alt="" src={previewImage} width={1200} height={675} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const SortableImage = ({
  image,
  index,
  onPreview,
  onRemove,
}: {
  image: string;
  index: number;
  onPreview: () => void;
  onRemove: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: image,
    });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      className={styles.imageWrapper}
    >
      <div className={styles.imageActionsWrapper}>
        <div className={styles.buttonsWrapper}>
          <button
            className={styles.previewButton}
            onClick={(e) => {
              e.stopPropagation();
              onPreview();
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <FaEye />
          </button>
          <button
            className={styles.deleteButton}
            onClick={(e) => {
              e.stopPropagation();
              const isConfirmed = window.confirm(
                "Are you sure you want to delete this photo?"
              );
              if (isConfirmed) {
                onRemove();
                return;
              } else {
                return;
              }
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <MdDelete />
          </button>
        </div>
        <Image alt="" src={image} width={1200} height={675} />
      </div>
    </div>
  );
};

// generate s3 presigned links for pending
// upload photos in pending bucket
// get the urls and send them to resize image
// resize images and put them in listing photos
// send event to delete photos from pending
// send back distribution links
// handle distribution links in front end.
